import React, { useRef, useEffect } from 'react'
import { connect } from "react-redux";
import workspace from "../canvas/core/workspace";
import { changeValuePopupInterface } from "../../interfaces/sqftInterfaces";
import useLongPress from "../../hocs/useLongPress";

const ChangeValuePopupWindow = (props) => {
  const {
    id,
    min,
    max,
    confirmCallback,
    increaseCallback,
    decreaseCallback,
    inputChangeCallback,
    value,
    position,
    title,
    shouldHideCloseButton
  } = props;

  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const hasAutoSelectedRef = useRef(false); // Flag to prevent repeated auto-select

  const onIncreaseLongPress = () => {
    increaseCallback(id);
  };

  const onIncreaseClick = (arg) => {
    increaseCallback(arg);
  };

  const onDecreaseLongPress = () => {
    decreaseCallback(id);
  };

  const onDecreaseClick = (arg) => {
    decreaseCallback(arg);
  };

  // Restore original semantics: inputChangeCallback(e)
  const onInputChange = (e) => {
    inputChangeCallback(e);
  };

  const defaultOptions = {
    shouldPreventDefault: true,
    delay: 500,
  };

  const increaseLongPressEvent = useLongPress(onIncreaseLongPress, onIncreaseClick, defaultOptions);
  const decreaseLongPressEvent = useLongPress(onDecreaseLongPress, onDecreaseClick, defaultOptions);

  const handleMouseLeave = () => {
    blurTimeoutRef.current = setTimeout(() => {
      if (inputRef.current && typeof inputRef.current.blur === 'function') {
        inputRef.current.blur();
      }
    }, 200);
  };

  const handleMouseEnter = () => {
    clearTimeout(blurTimeoutRef.current);
  };

  // Select on focus only once, when the popup is first opened
  const handleFocus = () => {
    if (inputRef.current && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      inputRef.current.select();
    }
  };

  useEffect(() => {
    if (!inputRef.current) return;

    const input = inputRef.current;

    const timerId = setTimeout(() => {
      if (!input) return;

      // Focus and select only once on mount
      if (!hasAutoSelectedRef.current) {
        if (typeof input.focus === 'function') {
          input.focus({ preventScroll: true });
        }
        if (typeof input.select === 'function') {
          input.select();
        }
        hasAutoSelectedRef.current = true;
      }
    }, 0);

    return () => {
      clearTimeout(timerId);
      clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  return (
    <div
      className={"change-value-popup tooltip tooltip-angle change-value-popup-" + (id)}
      style={{ left: position.left + 'px', top: position.top + 'px' }}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div className="tooltip-angle-nav">
        {title ? (
          <div className="tooltip-angle-header">
            {title}
          </div>
        ) : null}

        <input
          type="text"
          className="ui-input tooltip-angle-nav-input"
          min={min}
          max={max}
          value={value}
          onChange={onInputChange}
          onFocus={handleFocus}
          ref={inputRef}
        />

        <div
          className="tooltip-angle-nav-arrow tooltip-angle-nav-arrow-up"
          {...increaseLongPressEvent}
        >
          <svg className="triangle">
            <path d="M 22 8 L 40 30 L 5 30 z" stroke="#ffffff" strokeLinejoin="round" strokeWidth="8px"/>
            <path d="M 22 13 L 35 30 L 9 30 z" fill="#4A90E2" stroke="#4A90E2" strokeLinejoin="round" strokeWidth="8px"/>
          </svg>
        </div>

        <div
          className="tooltip-angle-nav-arrow tooltip-angle-nav-arrow-down"
          {...decreaseLongPressEvent}
        >
          <svg className="triangle">
            <path d="M 22 8 L 40 30 L 5 30 z" stroke="#ffffff" strokeLinejoin="round" strokeWidth="8px"/>
            <path d="M 22 13 L 35 30 L 9 30 z" fill="#4A90E2" stroke="#4A90E2" strokeLinejoin="round" strokeWidth="8px"/>
          </svg>
        </div>
      </div>

      {
        shouldHideCloseButton
          ? null
          : (
            <button
              className="ui-button10 ui-button10--green tooltip-angle-btn"
              onClick={() => confirmCallback()}
            >
              {workspace.intl.formatMessage({ id: "sqft.contextmenu.angleTooltipClose" })}
            </button>
          )
      }
    </div>
  );
};

ChangeValuePopupWindow.propTypes = changeValuePopupInterface;

const mapStateToProps = (store) => ({
  activeButton: store.sqftMenuState.activeButton,
});

export default connect(mapStateToProps)(ChangeValuePopupWindow);
